import { ArrowRight, Binoculars, ExternalLink, Eye, Leaf, Lightbulb, MapPin, Sparkles } from 'lucide-react';
import { dayTwoSections, resourceLinks, routeStops, sourceNotes } from '../data';
import type { ReadingSection } from '../types';
import { EcologyWeb } from './EcologyWeb';
import { FieldGuideStrip } from './FieldGuideStrip';
import { InteractiveMeander } from './InteractiveMeander';
import { ResourceShelf } from './ResourceShelf';

function sectionResources(section: ReadingSection) {
  return resourceLinks.filter((link) => section.resourceIds?.includes(link.id));
}

function SpreadResources({ section }: { section: ReadingSection }) {
  const links = sectionResources(section);
  if (links.length === 0) return null;

  return (
    <div className="spread-resources" aria-label={`${section.title} related resources`}>
      <b>继续了解</b>
      {links.map((link) => (
        <a href={link.url} key={link.id} rel="noreferrer" target="_blank">
          <span>{link.source}</span>
          {link.title}
          <ExternalLink aria-hidden="true" />
        </a>
      ))}
    </div>
  );
}

function BookSpread({ index, section }: { index: number; section: ReadingSection }) {
  return (
    <article className={`book-spread spread-${section.id}`}>
      <figure className="spread-media">
        {section.image && <img key={section.image} src={section.image} alt={section.imageAlt} />}
        {section.imageCredit && <figcaption>{section.imageCredit}</figcaption>}
      </figure>

      <div className="spread-copy">
        <span className="spread-number">{String(index + 1).padStart(2, '0')} · {section.eyebrow}</span>
        <h2>{section.title}</h2>
        <p className="spread-deck">{section.deck}</p>
        {section.body.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}

        {section.facts && (
          <div className="fact-ribbon" aria-label={`${section.title} key facts`}>
            {section.facts.map((fact) => (
              <span key={fact}>{fact}</span>
            ))}
          </div>
        )}

        {(section.lookFor || section.whyItMatters) && (
          <div className="spread-learning-grid">
            {section.lookFor && (
              <section className="learning-card">
                <h3>
                  <Eye aria-hidden="true" />
                  到现场怎么看
                </h3>
                <ul>
                  {section.lookFor.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            )}

            {section.whyItMatters && (
              <section className="learning-card">
                <h3>
                  <Lightbulb aria-hidden="true" />
                  为什么重要
                </h3>
                <p>{section.whyItMatters}</p>
                {section.fieldQuestion && <b>{section.fieldQuestion}</b>}
              </section>
            )}
          </div>
        )}

        {section.callout && (
          <aside className="parent-note">
            <Sparkles aria-hidden="true" />
            <div>
              <b>{section.callout.title}</b>
              <p>{section.callout.text}</p>
            </div>
          </aside>
        )}

        <SpreadResources section={section} />
      </div>
    </article>
  );
}

export function DayTwoPage() {
  return (
    <div className="day-page">
      <section className="hero-panel">
        <div className="hero-copy">
          <span className="day-marker">Day 2</span>
          <h1>弯弯的河，<br />草原的丝带</h1>
          <p>
            今天从草原营地走到高处，看见一条会拐弯、会移动、会留下秘密线索的河。
            你读懂它，就读懂了草原、水、动物和人的关系。
          </p>
          <div className="route-strip" aria-label="Day 2 route">
            {routeStops.map((stop, index) => (
              <span key={stop}>
                {stop}
                {index < routeStops.length - 1 && <ArrowRight aria-hidden="true" />}
              </span>
            ))}
          </div>
        </div>
        <figure className="hero-photo">
          <img
            key="/assets/day2_hero_morigele_overlook.jpg"
            src="/assets/day2_hero_morigele_overlook.jpg"
            alt="莫日格勒河高处俯瞰的写实场景图"
          />
          <figcaption>从高处看，河流像一条银色丝带，在草原上慢慢转弯。</figcaption>
        </figure>
      </section>

      <section className="book-spreads" aria-label="Day 2 book pages">
        {dayTwoSections.map((section, index) => (
          <div className="spread-cluster" key={section.id}>
            <BookSpread index={index} section={section} />
            {section.id === 'river' && <InteractiveMeander />}
            {section.id === 'grassland' && <EcologyWeb />}
          </div>
        ))}
      </section>

      <FieldGuideStrip />

      <ResourceShelf />

      <section className="discovery-panel">
        <div className="panel-heading">
          <Binoculars aria-hidden="true" />
          <div>
            <h2>今天只需要带走三个发现</h2>
            <p>任务不用多。真正看见，比填满空格更重要。</p>
          </div>
        </div>
        <div className="discovery-grid">
          <div>
            <b><MapPin aria-hidden="true" /> 找到一处河弯</b>
            <p>它像 S 形、月牙形，还是一条长长的丝带？</p>
          </div>
          <div>
            <b><Leaf aria-hidden="true" /> 找到湿地线索</b>
            <p>水面、深绿草、鸟、泥滩、浅水沟，找到哪一个？</p>
          </div>
          <div>
            <b><Binoculars aria-hidden="true" /> 发现动物位置</b>
            <p>牛羊马是在河边、坡地、道路旁，还是远处草地上？</p>
          </div>
        </div>
      </section>

      <details className="source-details">
        <summary>资料来源与核验说明</summary>
        <ul>
          {sourceNotes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
